import React from 'react';

interface PropertyDemandProps {
    localityName: string;
    projects: any[];
}

const PropertyDemand: React.FC<PropertyDemandProps> = ({ localityName, projects }) => {
    
    // Mocking Demand based on project types
    const typeCounts: Record<string, number> = {};
    projects.forEach(p => {
        const type = p.propertyType || 'Other';
        typeCounts[type] = (typeCounts[type] || 0) + 1;
    });

    const sortedTypes = Object.entries(typeCounts)
        .sort((a: any, b: any) => b[1] - a[1])
        .slice(0, 4);

    const totalCount = sortedTypes.reduce((acc, curr) => acc + curr[1], 0);

    // Mock search percentages
    const demandTypes = sortedTypes.map(([type, count]) => ({
        type,
        percentage: Math.round((count / totalCount) * 100)
    }));

    // Filter projects for Apartments and Villas
    const apartments = projects.filter(p => (p.propertyType || '').toLowerCase().includes('apartment'));
    const villas = projects.filter(p => (p.propertyType || '').toLowerCase().includes('villa'));

    return (
        <div className="container mx-auto px-4 py-20 max-w-7xl border-t border-slate-100">
            <div className="mb-10">
                <div className="flex items-center gap-2 mb-2">
                    <h2 className="text-2xl font-bold text-black uppercase tracking-tight">Property Demand</h2>
                    <i className="fa-solid fa-circle-info text-black opacity-20 text-sm cursor-help"></i>
                </div>
                <p className="text-black text-sm font-medium">What all are buyers searching in {localityName}</p>
            </div>

            <div className="relative group overflow-hidden">
                <div className="flex gap-4 overflow-x-auto no-scrollbar pb-8 scroll-smooth">
                    
                    {/* Overall Card */}
                    <div className="min-w-[320px] bg-white border border-slate-100 rounded-xl shadow-sm p-8 flex flex-col justify-between h-[440px]">
                        <div>
                            <h3 className="text-lg font-bold text-black mb-1">Overall</h3>
                            <p className="text-[12px] font-bold text-black mb-8">Most searched property types</p>

                            <div className="space-y-6">
                                {demandTypes.map((item, i) => (
                                    <div key={item.type} className="flex flex-col gap-2">
                                        <div className="flex justify-between items-baseline">
                                            <span className="text-[14px] font-black text-black"><span className="text-black mr-1 italic text-[12px]">#{i+1}</span>{item.type}</span>
                                            <span className="text-[12px] font-bold text-black">{item.percentage}% Searches</span>
                                        </div>
                                        <div className="w-full h-[6px] bg-slate-50 rounded-full overflow-hidden">
                                            <div className="h-full bg-black rounded-full" style={{ width: `${item.percentage}%` }}></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <button className="text-black font-black text-[10px] uppercase tracking-widest text-left mt-4 hover:opacity-70 transition-opacity underline">View all Properties</button>
                    </div>

                    {/* Apartments Card */}
                    <div className="min-w-[320px] bg-white border border-slate-100 rounded-xl shadow-sm p-8 flex flex-col justify-between h-[440px]">
                        <div>
                            <h3 className="text-lg font-bold text-black mb-1">Apartments</h3>
                            <p className="text-[12px] font-bold text-black mb-8">Most searched societies for Apartments</p>

                            <div className="space-y-6">
                                {apartments.slice(0, 5).map((p, i) => {
                                    // Calculate mock search share
                                    const share = i === 0 ? 87 : Math.max(2, Math.floor(13 / (apartments.length - 1 || 1)));
                                    return (
                                        <div key={p._id} className="flex flex-col gap-2">
                                            <div className="flex justify-between items-baseline gap-2">
                                                <span className="text-[14px] font-bold text-black leading-tight truncate max-w-[160px]"><span className="text-slate-300 mr-1 italic text-[12px]">#{i+1}</span>{p.name}</span>
                                                <span className="text-[12px] font-medium text-black whitespace-nowrap">{share}% Searches</span>
                                            </div>
                                            <div className="w-full h-[6px] bg-slate-50 rounded-full overflow-hidden">
                                                 <div className="h-full bg-[#B3D4FF] rounded-full" style={{ width: `${share}%` }}></div>
                                            </div>
                                        </div>
                                    );
                                })}
                                {apartments.length === 0 && <p className="text-xs italic text-black opacity-30">No apartment data available.</p>}
                            </div>
                        </div>
                        <button className="text-black font-black text-[10px] uppercase tracking-widest text-left mt-4 hover:opacity-70 transition-opacity underline">View all {apartments.length} Societies</button>
                    </div>

                    {/* Villas Card */}
                    <div className="min-w-[320px] bg-white border border-slate-100 rounded-xl shadow-sm p-8 flex flex-col justify-between h-[440px]">
                        <div>
                            <h3 className="text-lg font-bold text-black mb-1">Villas</h3>
                            <p className="text-[12px] font-bold text-black mb-8">Most searched societies for Villas</p>

                            <div className="space-y-6">
                                {villas.slice(0, 5).map((p, i) => {
                                    const share = i === 0 ? 75 : 25;
                                    return (
                                        <div key={p._id} className="flex flex-col gap-2">
                                            <div className="flex justify-between items-baseline gap-2">
                                                <span className="text-[14px] font-bold text-black leading-tight truncate max-w-[160px]"><span className="text-slate-300 mr-1 italic text-[12px]">#{i+1}</span>{p.name}</span>
                                                <span className="text-[12px] font-medium text-black whitespace-nowrap">{share}% Searches</span>
                                            </div>
                                            <div className="w-full h-[6px] bg-slate-50 rounded-full overflow-hidden">
                                                 <div className="h-full bg-[#B3D4FF] rounded-full" style={{ width: `${share}%` }}></div>
                                            </div>
                                        </div>
                                    );
                                })}
                                {villas.length === 0 && <p className="text-xs italic text-black opacity-30">No villa data available.</p>}
                            </div>
                        </div>
                        <button className="text-black font-black text-[10px] uppercase tracking-widest text-left mt-4 hover:opacity-70 transition-opacity underline">View all {villas.length} Societies</button>
                    </div>

                </div>

                {/* Right Scroll Indicator */}
                <div className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white border border-slate-100 shadow-lg rounded-full flex items-center justify-center cursor-pointer hover:bg-slate-50 transition-all z-10">
                    <i className="fa-solid fa-chevron-right text-slate-600 text-[12px]"></i>
                </div>
            </div>

            <style>{`
                .no-scrollbar::-webkit-scrollbar { display: none; }
                .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
            `}</style>
        </div>
    );
};

export default PropertyDemand;
